import {
	Avatar,
	Button,
	ChevronDownIcon,
	DropMenu,
	DropMenuDivider,
	DropMenuItem,
	DropMenuSection,
} from '@sailadv/hdata-ui';
import { Fragment, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useHasMultipleEntities } from '@/entities/boat';
import { useRoutes } from '@/entities/navigation';
import { useCanAccessGroupManagement, useIsPlant, useIsPlatformAdmin, useUser } from '@/entities/user';
import { useAppPrefix } from '@/shared/lib/hooks/use-app-prefix.hook';

import { userMenuSections } from '../../model/data/user-menu-navigation';
import { UserMenuFooter } from './ui/user-menu-footer';

const UserMenuComponent = () => {
	const { t } = useTranslation('common');
	const { username } = useUser();
	const isPlant = useIsPlant();
	const hasMultipleEntities = useHasMultipleEntities();
	const { management } = useRoutes();
	const canAccessGroupManagement = useCanAccessGroupManagement();
	const isPlatformAdmin = useIsPlatformAdmin();
	const prefix = useAppPrefix();

	const sections = useMemo(
		() =>
			userMenuSections({ isPlant, management, hasMultipleEntities, canAccessGroupManagement, isPlatformAdmin })
				.map((section) => ({ ...section, links: section.links.filter(({ show }) => show) }))
				.filter(({ links }) => links.length),
		[isPlant, management, hasMultipleEntities, canAccessGroupManagement, isPlatformAdmin],
	);

	return (
		<DropMenu
			aria-label={username}
			placement="bottom end"
			size="md"
			footer={<UserMenuFooter />}
			trigger={
				<Button variant="ghost" size="sm" aria-label={username}>
					<Avatar size="sm" name={username} details className="max-sm:[&>span+span]:hidden" />
					<ChevronDownIcon size="sm" />
				</Button>
			}
		>
			{sections.map(({ label, links }, index) => (
				<Fragment key={label}>
					{index > 0 && <DropMenuDivider />}
					<DropMenuSection label={t(label)}>
						{links.map(({ label: linkLabel, Icon, href }) => (
							<DropMenuItem key={linkLabel} id={linkLabel} href={`${prefix}${href}`} leading={<Icon />}>
								{t(linkLabel)}
							</DropMenuItem>
						))}
					</DropMenuSection>
				</Fragment>
			))}
		</DropMenu>
	);
};

export default memo(UserMenuComponent);
